<template>
    <div class="flex flex-col">
        <div class="mt-4">
            <div v-if="visibleCards.length === 0">
                <EmptyState
                    v-if="hasActiveFilters"
                    title="No cards found matching filter criteria"
                    description="Try changing your filter selections."
                    :hasAction="true"
                    action-label="Clear filters"
                    @action="clearAllFilters"
                />
                <EmptyState
                    v-else
                    title="No cards found!"
                    description="Start adding cards to your wallet to see everything in one place."
                    :hasAction="true"
                    action-label="Add an account"
                    @action="handleAddAccount"
                />
            </div>
            <!-- ungrouped cards: -->
            <div v-else-if="!walletStore.groupBy" class="flex flex-col gap-4">
                <div v-for="(group, i) in visibleCards" :key="group.card_id">
                    <template v-if="groupSameCards">
                        <WalletTableMobileCardGroup
                            :group="group"
                            @edit="handleEdit"
                            @delete="handleDelete"
                            @view-details="handleViewDetails"
                            :totalCards="visibleCards.length"
                        />
                    </template>
                    <template v-else>
                        <WalletTableMobileCard
                            :key="group.id"
                            :card="group"
                            :index="i"
                            @edit="handleEdit"
                            @delete="handleDelete"
                            @view-details="handleViewDetails"
                        />
                    </template>
                </div>
            </div>
            <!-- grouped but not subgrouped cards: -->
            <div v-else>
                <template v-if="!walletStore.subgroupBy">
                    <div
                        v-for="(groupCards, groupName, index) in groupedCards"
                        :key="groupName"
                    >
                        <div
                            class="group-header pb-2 pt-4 text-base font-medium text-slate-700 dark:text-slate-300"
                        >
                            <span class="flex items-center gap-2">
                                <WalletTableInstitutionTitle
                                    v-if="
                                        walletStore.groupBy.id ===
                                        'institution_name'
                                    "
                                    :item="groupCards"
                                    :name="groupName"
                                    :groupSameCards="groupSameCards"
                                />
                                <span v-else>
                                    {{
                                        formatEntityName(
                                            $filters.titleCase(groupName)
                                        )
                                    }}
                                </span>
                                <span class="text-slate-500 dark:text-slate-400">
                                    {{ groupCards.length }}
                                </span>
                            </span>
                        </div>
                        <div
                            v-for="(group, index) in groupCards"
                            :key="group.card_id"
                            class="mb-4"
                        >
                            <template v-if="groupSameCards">
                                <WalletTableMobileCardGroup
                                    :group="group"
                                    @edit="handleEdit"
                                    @delete="handleDelete"
                                    @view-details="handleViewDetails"
                                    :index="index"
                                    :totalCards="groupCards.length"
                                />
                            </template>
                            <template v-else>
                                <WalletTableMobileCard
                                    :key="group.id"
                                    :card="group"
                                    :index="index"
                                    @edit="handleEdit"
                                    @delete="handleDelete"
                                    @view-details="handleViewDetails"
                                />
                            </template>
                        </div>
                    </div>
                </template>
                <!-- grouped and subgrouped cards: -->
                <template v-else>
                    <!-- Primary groups (first level) -->
                    <template
                        v-for="(
                            subgroupsObj, primaryGroupName, index
                        ) in groupedCards"
                        :key="primaryGroupName"
                    >
                        <!-- Primary group header -->
                        <div
                            class="group-header pt-4 text-base font-medium text-slate-700 dark:text-slate-300"
                        >
                            {{ formatEntityName(primaryGroupName) }}
                        </div>

                        <!-- Subgroups (second level) -->
                        <div
                            v-for="(
                                subgroupCards, subgroupName
                            ) in subgroupsObj"
                            :key="`${primaryGroupName}-${subgroupName}`"
                        >
                            <!-- Subgroup header -->
                            <div
                                class="subgroup-header pb-4 pt-3 text-base font-medium text-slate-700 dark:text-slate-300"
                            >
                                <span class="flex items-center gap-2">
                                    <WalletTableInstitutionTitle
                                        v-if="
                                            walletStore.subgroupBy.id ===
                                            'institution_name'
                                        "
                                        :item="subgroupCards"
                                        :name="subgroupName"
                                        :groupSameCards="groupSameCards"
                                    />
                                    <span v-else>
                                        {{
                                            formatEntityName(
                                                $filters.titleCase(subgroupName)
                                            )
                                        }}
                                    </span>
                                    <span class="text-slate-500 dark:text-slate-400">
                                        {{ subgroupCards.length }}
                                    </span>
                                </span>
                            </div>

                            <!-- Subgroup cards -->
                            <div
                                v-for="(group, index) in subgroupCards"
                                :key="group.card_id"
                                class="mb-4"
                            >
                                <template v-if="groupSameCards">
                                    <WalletTableMobileCardGroup
                                        :group="group"
                                        @edit="handleEdit"
                                        @delete="handleDelete"
                                        @view-details="handleViewDetails"
                                        :totalCards="subgroupCards.length"
                                        :index="index"
                                    />
                                </template>
                                <template v-else>
                                    <WalletTableMobileCard
                                        :key="group.id"
                                        :card="group"
                                        :index="index"
                                        @edit="handleEdit"
                                        @delete="handleDelete"
                                        @view-details="handleViewDetails"
                                    />
                                </template>
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
        <div
            class="flex items-center justify-center text-sm pb-2"
            v-show="hiddenCardsCount > 0 && visibleCards.length > 0"
        >
            <Transition name="fade" mode="out-in">
                <button
                    v-if="!showHiddenCards"
                    @click.prevent="setShowHiddenCards(true)"
                    class="flex btn-base btn-transparent !py-1 !text-slate-500 min-w-[200px] justify-center"
                    role="button"
                    id="hs-wallet-show-hidden-cards"
                >
                    <Icon icon="eye-on" class="h-5 w-5" />
                    <span>
                        Show {{ hiddenCardsCount }} hidden card{{
                            hiddenCardsCount > 1 ? 's' : ''
                        }}
                    </span>
                </button>
                <button
                    v-else
                    @click.prevent="setShowHiddenCards(false)"
                    class="flex btn-base btn-transparent !py-1 !text-slate-500 min-w-[200px] justify-center"
                    role="button"
                    id="hs-wallet-hide-hidden-cards"
                >
                    <Icon icon="eye-off" class="h-5 w-5" />
                    <span>Collapse hidden cards</span>
                </button>
            </Transition>
        </div>
    </div>
    <WalletTableCardDetailsModal
        :show="showAccountSidebar"
        @close="setAccountSidebar(false)"
        @open-edit-card="handleOpenEditCard"
    />
</template>
<script setup>
import { watch } from 'vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import WalletTableMobileCardGroup from '@/components/wallet/wallet-table-mobile-card-group.vue';
import WalletTableMobileCard from '@/components/wallet/wallet-table-mobile-card.vue';
import WalletTableInstitutionTitle from '@/components/wallet/wallet-table-institution-title.vue';
import WalletTableCardDetailsModal from '@/components/wallet/modals/wallet-table-card-details-modal.vue';
import { formatEntityName } from '@/utils/entityNameFormatter.js';
const emit = defineEmits(['edit', 'delete', 'view-details', 'add-account']);
const walletStore = useWalletStore();

const {
    groupSameCards,
    hasActiveFilters,
    showHiddenCards,
    visibleCards,
    groupedCards,
    hiddenCardsCount,
    showAccountSidebar,
} = storeToRefs(walletStore);

const {
    clearAllFilters,
    setShowHiddenCards,
    setSubgroupBy,
    setAccountSidebar,
} = walletStore;

watch(
    () => walletStore.groupBy,
    () => {
        setSubgroupBy(null);
    }
);

const handleEdit = (card) => {
    emit('edit', card);
};

const handleDelete = (card) => {
    emit('delete', card);
};

const handleViewDetails = (card) => {
    emit('view-details', card);
};

const handleAddAccount = () => {
    emit('add-account');
};

const handleOpenEditCard = (cardId, options = {}) => {
    setAccountSidebar(false);
    emit('edit', {
        id: cardId,
        editingProductChange: options.editingProductChange || null,
    });
};
</script>
